import React, { useContext, useEffect, useRef, useState } from 'react';
import NotificationBanner from '../../components/notification-banner/notification-banner';
import { Trans, useTranslation } from 'react-i18next';
import RouteLink from '../../components/route-link';
import { gotoSignIn, isGovCloudRegion } from '../../utils/helpers';
import {
  DropdownMenu,
  FormContainer,
  InputField,
  Recaptcha,
  trialRegistrationValidation,
  registrationValidation,
  SubmitButton,
} from '../../components/forms';
import { Box, Space, Stack, Text } from '@tonic-ui/react';
import { countries } from '../../utils/countries';
import TCCheckbox from './tc-checkbox';
import { supportedLocales } from '../../locales/supportedLocales';
import { getTimeZoneAreaLocation } from '../../utils/timezone';
import { Auth } from '@aws-amplify/auth';
import filterMessage from '../../utils/message-filter';
import { AuthContext } from '../../context/AuthContext';
import { useHistory } from 'react-router-dom';
import { RecaptchaRef } from '../../components/forms/recaptcha';
import { shallowEqual, useSelector } from 'react-redux';
import { getCommonType } from '../../store';

let deploymentTarget = '';
let recaptchaSitekey = '';

switch (DEPLOYMENT_TARGET) {
  case 'prod':
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_PROD || '';
    break;
  case 'stage':
    deploymentTarget = 'staging-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_STAGING || '';
    break;
  case 'drsim':
    deploymentTarget = 'drsim-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_DRSIM || '';
    break;
  case 'govcloud-prod':
    deploymentTarget = 'govcloud-prod-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_GOVCLOUD_PROD || '';
    break;
  case 'govcloud-staging':
    deploymentTarget = 'govcloud-staging-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_GOVCLOUD_STAGING || '';
    break;
  case 'alpha':
  case 'dev':
    deploymentTarget = 'dev-';
    recaptchaSitekey = process.env.RECAPTCHA_SITEKEY_DEV || '';
    break;
  default:
    console.log('Should never reach here');
}
type RegistrationPageProps = {
  isTrialPage: boolean;
};

interface FormProps {
  email: string;
  name: string;
  country: string;
  password: string;
  confirmPassword: string;
  recaptcha: string;
  recaptchaSitekey: string;
  termsAndConditions: false;
}

const RegistrationForm = ({ isTrialPage }: RegistrationPageProps) => {
  const history = useHistory();
  const { t } = useTranslation();
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const focusRef = useRef<HTMLElement | null>(null);
  const {
    theme: { colorMode },
    locale,
  } = useSelector(
    (state) => ({
      theme: getCommonType(state, 'theme'),
      locale: getCommonType(state, 'locale'),
    }),
    shallowEqual
  );
  const [refererUrl, setRefererUrl] = useState('');
  const [formErrors, setFormErrors] = useState({
    error: '',
  });
  const [selectedCountry, setSelectedCountry] = useState('');
  const { eventDistributor } = useContext(AuthContext);

  // Note that the line below explicitly doesn't use the react-router history because
  // for some reason beyond my ken it didn't work. This does, though.
  const { invitationToken = '', email: defaultEmail = '' } = window.history.state || {};

  useEffect(() => {
    if (focusRef.current) {
      focusRef.current = focusRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    const findReferrerCookie =
      document.cookie.length > 1 &&
      document.cookie.split('; ').find((cookie) => cookie.startsWith('_c1Ref'));
    const referrerPathname = findReferrerCookie ? findReferrerCookie.split('_c1Ref=')[1] : '';

    setRefererUrl(`${document.referrer}${decodeURIComponent(referrerPathname.slice(1))}`);
  }, []);

  const handleSubmit = async ({
    email,
    name,
    country,
    password,
    setSubmitting,
    resetForm,
    recaptcha,
  }) => {
    setFormErrors({
      ...formErrors,
      error: '',
    });

    const storedLocale = localStorage.getItem('C1C_locale');
    const preferredLanguage =
      storedLocale && supportedLocales.includes(storedLocale) ? storedLocale : 'en';

    // In Area/Location (city) format
    const timeZoneString = getTimeZoneAreaLocation();
    try {
      const { codeDeliveryDetails = {}, userConfirmed = false } = await Auth.signUp({
        username: email,
        password: password,
        attributes: {
          name: name,
          locale: preferredLanguage,
          zoneinfo: timeZoneString,
          'custom:country': country,
        },
        validationData: {
          recaptchaToken: recaptcha,
          validationToken: invitationToken,
        },
      });

      return history.push('/confirmation', {
        name,
        email,
        result: {
          // need to explicitly take only some attributes as others
          // are complex and can't be pushed through history
          codeDeliveryDetails: codeDeliveryDetails,
          userConfirmed: userConfirmed,
        },
      });
    } catch (error: unknown) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errMsg = !error.message ? error : error.message;
      console.error('Error: ', errMsg);

      setFormErrors({
        ...formErrors,
        error: filterMessage(error),
      });

      setSubmitting(false);
      resetForm();
      recaptchaRef.current?.resetRecaptcha();
    }
  };

  const defaultValues: FormProps = {
    email: defaultEmail,
    name: '',
    country: '',
    password: '',
    confirmPassword: '',
    recaptcha: '',
    recaptchaSitekey: '',
    termsAndConditions: false,
  };
  const labelStyles = isTrialPage ? { fontWeight: 300 } : {};
  return (
    <>
      <FormContainer
        id="registration-form"
        customValidation={isTrialPage ? trialRegistrationValidation : registrationValidation}
        initialValues={defaultValues}
        enableReinitialize={true}
        submitAction={(formPayload) => handleSubmit(formPayload)}
        validateOnChange={true}
        maxWidth={'850px'}
        {...(isTrialPage && {
          maxWidth: '550px',
          fontFamily: 'interstate',
          fontWeight: '300',
        })}
      >
        <>
          <Stack spacing="4x" {...(isTrialPage && { fontFamily: 'opensans' })}>
            {isTrialPage ? (
              <Text fontSize="24px" lineHeight="35px" marginBottom="6x">
                <Trans i18nKey="signUpCloudOneText5" />
              </Text>
            ) : (
              <Text size="2xl" lineHeight="xl" marginBottom="28px">
                {t('signUpCloudOne')}
              </Text>
            )}
          </Stack>
          <Stack spacing="2x">
            {formErrors.error && (
              <Box marginBottom="4x">
                {formErrors.error.includes('emailAlreadyExists') ? (
                  <NotificationBanner
                    name="registration-form-user-already-exists"
                    severity="error"
                    variant="outline"
                    shouldOpen={true}
                    htmlElement={
                      <Trans
                        i18nKey={`cognitoErrorMessages.emailAlreadyExists`}
                        components={{
                          signinLink: <RouteLink href="/" />,
                        }}
                      />
                    }
                  />
                ) : (
                  <NotificationBanner
                    name="registration-form"
                    severity="error"
                    variant="outline"
                    shouldOpen={true}
                    bannerText={formErrors.error}
                  />
                )}
              </Box>
            )}
            <Box>
              <InputField
                id="business-email"
                type="email"
                label={t('businessEmail')}
                name="email"
                isRequired={true}
                ref={focusRef}
                labelStyles={labelStyles}
              />
            </Box>
            <Box>
              <InputField
                id="name"
                type="text"
                label={t('name')}
                name="name"
                isRequired={true}
                labelStyles={labelStyles}
              />
            </Box>
            <DropdownMenu
              options={countries}
              label={t('country')}
              placeholder={t('selectCountry')}
              name="country"
              maxHeight={200}
              isRequired={true}
              isSearchable={true}
              onChange={(selectedId) => setSelectedCountry(selectedId)}
            />
            <Box>
              <InputField
                id="password"
                type="password"
                label={t('password')}
                name="password"
                isRequired={true}
                labelStyles={labelStyles}
                infoPopoverContent={
                  <>
                    <Text>{t('signupPasswordError.passwordInfoIconDescription1')}</Text>
                    <Box textIndent="initial">
                      <ul>
                        <li>{t('signupPasswordError.passwordInfoIconDescription2')}</li>
                        <li>{t('signupPasswordError.passwordInfoIconDescription3')}</li>
                        <li>{t('signupPasswordError.passwordInfoIconDescription4')}</li>
                        <li>{t('signupPasswordError.passwordInfoIconDescription5')}</li>
                      </ul>
                    </Box>
                  </>
                }
              />
            </Box>
            <Box>
              <InputField
                id="confirm-password"
                type="password"
                label={t('confirmPassword')}
                name="confirmPassword"
                isRequired={true}
              />
            </Box>
          </Stack>
          <Stack spacing="4x" marginTop="4x">
            {/* Added min height to match how much space recaptcha will add when it loads to minimize reflow*/}
            <Box minHeight="84px">
              <Recaptcha
                ref={recaptchaRef}
                name="recaptcha"
                theme={colorMode}
                siteKey={recaptchaSitekey}
                locale={locale}
              />
            </Box>
            <Box>
              <TCCheckbox name="termsAndConditions" countryId={selectedCountry} />
            </Box>
            <Box>
              <SubmitButton
                label={t('signUp')}
                id="registration-form-submit-btn"
                variant="emphasis"
                width="100%"
                height="40px"
                lineHeight="normal"
              />
            </Box>
            <Text
              alignSelf="center"
              marginTop="6x"
              {...(isTrialPage && { fontFamily: 'opensans' })}
            >
              {t('alreadyUser')}
              <Space width="1x" />
              <RouteLink href="/">{t('signIn')}</RouteLink>
            </Text>
          </Stack>
        </>
      </FormContainer>
      {!isGovCloudRegion && (
        <iframe
          frameBorder="0"
          width="1"
          height="1"
          name="registration"
          title="c1c_tealium_iframe"
          id="c1c_tealium_iframe"
          src={`https://tsm.${deploymentTarget}cloudone.trendmicro.com/_auth/static-${BUILD_TIMESTAMP}/assets/tealium/index.html?referrer=${refererUrl}&url=${document.URL}`}
        />
      )}
    </>
  );
};

RegistrationForm.displayName = 'RegistrationForm';
export default RegistrationForm;
